import { detectOS, isMobileDevice } from '../core/detector';
import type { OperatingSystem, WindowFocusProtectionLevel } from '../types';
import { isScreenshotPredecessor } from './keys';
import { ScreenshotOverlay } from './overlay';

/**
 * Manages screenshot key event listening and overlay display.
 * Detects predecessor key combinations (e.g., Cmd+Shift on Mac) before
 * the user can complete the screenshot shortcut.
 * Optionally shows the overlay when the user leaves the browser window,
 * controlled by {@link WindowFocusProtectionLevel}.
 */
export class ScreenshotListener {
    private keyboardOverlay: ScreenshotOverlay;
    private focusOverlay: ScreenshotOverlay;
    private os: OperatingSystem;
    private isListening = false;
    private keyboardEnabled: boolean;
    private focusLevel: WindowFocusProtectionLevel;

    constructor(
        customHtml: string,
        keyboardEnabled: boolean,
        focusLevel: WindowFocusProtectionLevel,
        focusCustomHtml?: string
    ) {
        this.keyboardOverlay = new ScreenshotOverlay(customHtml, false);
        this.focusOverlay = new ScreenshotOverlay(focusCustomHtml ?? customHtml, true);
        this.os = detectOS();
        this.keyboardEnabled = keyboardEnabled;
        this.focusLevel = focusLevel;
    }

    /**
     * Starts listening for screenshot-related events
     */
    start(): void {
        if (this.isListening) {
            return;
        }

        this.isListening = true;

        if (this.keyboardEnabled) {
            document.addEventListener('keydown', this.handleKeyDown, true);
        }

        if (this.focusLevel === 'blur' && !isMobileDevice()) {
            window.addEventListener('blur', this.handleFocusLost);
            window.addEventListener('focus', this.handleFocusRestored);
        } else if (this.focusLevel === 'visibilitychange') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    /**
     * Stops listening and cleans up
     */
    stop(): void {
        if (!this.isListening) {
            return;
        }

        this.isListening = false;

        document.removeEventListener('keydown', this.handleKeyDown, true);
        window.removeEventListener('blur', this.handleFocusLost);
        window.removeEventListener('focus', this.handleFocusRestored);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.keyboardOverlay.destroy();
        this.focusOverlay.destroy();
    }

    private handleKeyDown = (event: KeyboardEvent): void => {
        if (isScreenshotPredecessor(event, this.os)) {
            this.keyboardOverlay.show();
        }
    };

    private handleFocusLost = (): void => {
        this.focusOverlay.show();
    };

    private handleFocusRestored = (): void => {
        this.focusOverlay.hide();
    };

    private handleVisibilityChange = (): void => {
        if (document.hidden) {
            this.focusOverlay.show();
        } else {
            this.focusOverlay.hide();
        }
    };
}
